import React, { useEffect, useState } from "react";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { NeutralColor, SecondaryColor } from "node-library/dist/colors";

import { Severity } from "node-library/dist/constants";

import AddIcon from "@mui/icons-material/Add";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { hasPermission, Permissions } from "node-library/dist/permissions";
import {
	BlackButton,
	CustomIconButton,
	GreenButton,
	KebabDropdownButton,
} from "../../../../common-components/button";
import CreateSpecFolderModal from "../../../../common-components/modals/createSpecFolderModal";
import DeleteSpecificationConfirmModal from "../../../../common-components/modals/deleteSpecificationConfirmModal";
import { NavBar } from "../../../../common-components/navbar";
import {
	DeleteIcon,
	DownloadIcon,
	EditIcon,
	FolderIcon,
	UploadIcon,
} from "../../../../common-components/svgIcons";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { downloadSpecificationFile } from "../../../../helpers/frontendFileOperationHelper";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import SpecificationSubFolderRow from "../modal/SpecificationSubFolderRow";
import UploadSpecificationDrawer from "../../../../common-components/drawers/uploadSpecificationDrawer";
import { Radio, Tooltip } from "@mui/material";
import { getSpecificationIcon } from "../../../../helpers/specificationHelper";
import { getFileDisplayName } from "node-library/dist/helpers/file_helper";

interface Props {
	user: FrontendUserInfo;
	orgDetailsSelectedCategory: FrontendCompanyTestCategory;

	orgSpecifications: Array<FrontendSampleSpecification>;
	setOrgSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;

	selectedMatrix: FrontendCompanySampleMatrix;

	currentFolder: FrontendSampleSpecificationDirectory | undefined;
	setCurrentFolder: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecificationDirectory | undefined>
	>;

	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;

	isInsideModal: boolean;
	setCreateSpecModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
	openSpecReadingDrawer?: (specFile: FrontendSampleSpecification) => void;

	selectedSpecificationId: string | undefined;
	setSelectedSpecificationId?: React.Dispatch<
		React.SetStateAction<string | undefined>
	>;
}

export default function SpecificationFilesAndFoldersContents(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		user,
		orgDetailsSelectedCategory,
		specDirectories,
		selectedMatrix,
		currentFolder,
		setCurrentFolder,
		isInsideModal,
		setCreateSpecModalOpen,
		setSpecDirectories,
		setOrgSpecifications,
		openSpecReadingDrawer,
	} = props;

	const matrixId: string = selectedMatrix.matrixId;

	const [deleteSpecModalOpen, setDeleteSpecModalOpen] =
		useState<boolean>(false);
	const [createFolderModalOpen, setCreateFolderModalOpen] =
		useState<boolean>(false);

	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {},
	);

	const [highlightedSubFolderId, setHighlightedSubFolderId] = useState<
		string | undefined
	>();

	const [loading, setLoading] = useState<boolean>(false);

	const currentFolderSubFolders = matrixId
		? specDirectories.filter((dir) => {
				if (!currentFolder) {
					return (
						dir.matrixId === matrixId && dir.parentDirectoryId == undefined
					);
				}
				return (
					dir.matrixId === matrixId &&
					dir.parentDirectoryId === currentFolder.directoryId
				);
			})
		: [];
	const currentFolderSpecs = matrixId
		? props.orgSpecifications.filter((spec) => {
				// Define the target ID: the directory's ID if it exists, otherwise null
				const targetId = currentFolder ? currentFolder.directoryId : null;

				return (
					spec.matrixId === matrixId &&
					// Matches if spec.parentDirectoryId is the targetId,
					// OR if both are "falsy" (null/undefined)
					(spec.parentDirectoryId === targetId ||
						(!spec.parentDirectoryId && !targetId))
				);
			})
		: [];

	const userHasFolderAccess: boolean =
		user?.role &&
		hasPermission(user?.role?.permissions, Permissions.specs.folders)
			? true
			: false;

	useEffect(() => {
		setCurrentFolder(undefined);
		setHighlightedSubFolderId(undefined);
	}, [props.orgDetailsSelectedCategory]);

	async function deleteSpecification(specificationId: string, inUse: boolean) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/delete-specification",
				{
					specificationId,
				},
			);

			setOrgSpecifications((prevSpecs) =>
				prevSpecs.filter((spec) => spec.specificationId !== specificationId),
			);

			if (!inUse) {
				openSnackbar(
					translation("FileDeleted"),
					Severity.ERROR,
					undefined,
					<RedTrashIcon />,
				);
			} else {
				openSnackbar(
					translation("SpecificationDeactivated"),
					Severity.ERROR,
					undefined,
					<RedTrashIcon />,
				);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	async function reactivateSpecification(specificationId: string) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/reactivate-specification",
				{
					specificationId,
				},
			);

			setOrgSpecifications((prevSpecifications) =>
				prevSpecifications.map(
					(spec) =>
						spec.specificationId === specificationId
							? { ...spec, active: true } // Update the 'active' field for the matched specification
							: spec, // Leave other specifications unchanged
				),
			);

			openSnackbar(
				translation("SpecificationReactivated"),
				Severity.ERROR,
				undefined,
				<RedTrashIcon />,
			);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	const getBreadcrumbs = () => {
		const crumbs: Array<FrontendSampleSpecificationDirectory> = [];
		let tempFolder = currentFolder;

		while (tempFolder) {
			crumbs.unshift(tempFolder); // Add to the beginning of array
			// Find the parent of the current temp folder
			tempFolder = specDirectories.find(
				(dir) => dir.directoryId === tempFolder?.parentDirectoryId,
			);
		}
		return crumbs;
	};

	const breadcrumbs = getBreadcrumbs();

	const navbarItems: Array<{
		label: string;
		onClick: () => void;
		isCurrentItem: boolean;
	}> = selectedMatrix
		? [
				// 1. The Root (Matrix)
				{
					label: selectedMatrix.subMatrix,
					onClick: () => setCurrentFolder(undefined),
					isCurrentItem: currentFolder === undefined,
				},
				// 2. The Folders in between
				...breadcrumbs.map((folder) => ({
					label: folder.name,
					onClick: () => setCurrentFolder(folder),
					isCurrentItem: currentFolder?.directoryId === folder.directoryId,
				})),
			]
		: [];

	const currentFolderContents: React.ReactNode = matrixId ? (
		<div className={matrixId ? `` : `profile-specification-not-selected`}>
			<div className="profile-spec-file-section-header">
				<div>
					<div className="typography-body-02 typography-bold">
						{translation("FilesAndFolders")}
					</div>
				</div>
				<div>
					{userHasFolderAccess && (
						<GreenButton
							id={"profile-create-folder-button"}
							size="small"
							buttonClassName="button-with-left-icon"
							onClick={() => {
								setCreateFolderModalOpen(true);
							}}
						>
							<AddIcon />
							<span
							// className="new-order-collapsible-text"
							>
								{translation("Folder")}
							</span>
						</GreenButton>
					)}
					{!isInsideModal && setCreateSpecModalOpen && (
						<BlackButton
							id={"profile-spec-upload-button"}
							size="small"
							buttonClassName="button-with-left-icon"
							onClick={() => {
								setCreateSpecModalOpen(true);
							}}
						>
							<UploadIcon strokeColor="white" size={24} />
							<span
							// className="new-order-collapsible-text"
							>
								{translation("Upload")}
							</span>
						</BlackButton>
					)}
				</div>
			</div>
			<div
				style={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					marginBottom: "10px",
					marginLeft: "15px",
				}}
			>
				<CustomIconButton
					id="spec-folder-back-button"
					size="small"
					onClick={() => {
						if (currentFolder) {
							const parentFolder:
								| FrontendSampleSpecificationDirectory
								| undefined = specDirectories.find((dir) => {
								return dir.directoryId === currentFolder.parentDirectoryId;
							});
							setCurrentFolder(parentFolder);
						}
					}}
					icon={<KeyboardArrowLeftIcon />}
					disabled={currentFolder ? false : true}
				></CustomIconButton>

				<NavBar items={navbarItems}></NavBar>
			</div>

			<div id="profile-files-selection-container">
				{currentFolderSubFolders.map((dir) => {
					return (
						<SpecificationSubFolderRow
							key={`sub-sub-folder-${dir.directoryId}`}
							label={dir.name}
							icon={
								<FolderIcon
									strokeColor={SecondaryColor.SECONDARY_300}
									fillColor={SecondaryColor.SECONDARY_LV10}
								/>
							}
							selected={highlightedSubFolderId == dir.directoryId}
							onRowClick={() => {
								setHighlightedSubFolderId(dir.directoryId);
							}}
							onDoubleClick={() => {
								setHighlightedSubFolderId(undefined);
								setCurrentFolder(dir);
							}}
						/>
					);
				})}
				{currentFolderSpecs.map((specs) => {
					let deleteOrToggleAction = () => {
						setProceedAction(() => () => {
							deleteSpecification(specs.specificationId, specs.inUse);
						});
						setDeleteSpecModalOpen(true);
					};
					let deleteOrToggleSpecStatusOptionNode: React.ReactNode = (
						<div className="icon-dropdown-menu-item">
							<DeleteIcon></DeleteIcon>
							{translation("Delete")}
						</div>
					);
					if (specs.inUse) {
						if (specs.active) {
							deleteOrToggleAction = () => {
								//do not show modal
								deleteSpecification(specs.specificationId, specs.inUse);
							};
							deleteOrToggleSpecStatusOptionNode = (
								<div className="icon-dropdown-menu-item">
									<DeleteIcon></DeleteIcon>
									{translation("Deactivate")}
								</div>
							);
						} else {
							deleteOrToggleAction = () => {
								reactivateSpecification(specs.specificationId);
							};
							deleteOrToggleSpecStatusOptionNode = (
								<div className="icon-dropdown-menu-item">
									<DeleteIcon></DeleteIcon>
									{translation("Reactivate")}
								</div>
							);
						}
					}

					let toggleActionOption = [
						{
							key: `delete-specification-${specs.specificationId}`,
							onClick: deleteOrToggleAction,
							children: deleteOrToggleSpecStatusOptionNode,
						},
					];

					// 2. Conditionally push the Download option
					if (specs.canDownload) {
						toggleActionOption.push({
							key: `download-specification-${specs.specificationId}`,
							onClick: () => {
								downloadSpecificationFile(specs);
							},
							children: (
								<div className="icon-dropdown-menu-item">
									<DownloadIcon size={20} />
									{translation("Download")}
								</div>
							),
						});
					}

					// 3. Push the Create/Edit Reading option
					toggleActionOption.push({
						key: `reading-action-${specs.specificationId}`,
						onClick: () => {
							// Your logic to open the editable table or modal
							if (openSpecReadingDrawer) {
								openSpecReadingDrawer(specs);
							}
						},
						children: (
							<div className="icon-dropdown-menu-item">
								{specs.readingFileId ? (
									<>
										<EditIcon
											strokeColor={NeutralColor.NEUTRAL_GREY_950}
											size={20}
										/>
										{translation("EditReading")}
									</>
								) : (
									<>
										<AddCircleOutlineOutlinedIcon
											sx={{ width: 20, height: 20 }}
										/>
										{translation("CreateReading")}
									</>
								)}
							</div>
						),
					});
					let specIcon: React.ReactNode = getSpecificationIcon(
						specs,
						translation,
					);

					return (
						<div className="profile-spec-file-row" key={specs.specificationId}>
							<div
								className="typography-body-02"
								style={{
									color: specs.active
										? NeutralColor.NEUTRAL_GREY_950
										: NeutralColor.NEUTRAL_GREY_500,
									display: "flex",
									alignItems: "center",
									gap: "4px",
								}}
							>
								{specIcon}
								{`${getFileDisplayName(specs.fileName, specs.format)}`}
							</div>
							{props.setSelectedSpecificationId && (
								<Radio
									checked={
										props.selectedSpecificationId === specs.specificationId
									}
									value={specs.specificationId}
									onChange={(e) => {
										props.setSelectedSpecificationId!(specs.specificationId);
									}}
								/>
							)}
							{!isInsideModal && (
								<KebabDropdownButton
									menuOptions={toggleActionOption}
									id={`specification-more-action-menu-${specs.specificationId}`}
									onClick={(e) => {}}
								></KebabDropdownButton>
							)}
						</div>
					);
				})}

				{currentFolderSubFolders.length == 0 &&
				currentFolderSpecs.length == 0 ? (
					<div style={{ color: NeutralColor.NEUTRAL_GREY_500 }}>
						{translation("NoSpecificationFilesInFolder")}
					</div>
				) : (
					<></>
				)}
			</div>
		</div>
	) : (
		<></>
	);

	// const uploadModal: React.ReactNode =

	return (
		<>
			{currentFolderContents}
			{
				<CreateSpecFolderModal
					open={createFolderModalOpen}
					setOpen={setCreateFolderModalOpen}
					existingFolder={undefined}
					testCategoryId={props.orgDetailsSelectedCategory.testCategoryId}
					selectedMatrix={selectedMatrix}
					parentFolder={currentFolder}
					setSpecDirectories={setSpecDirectories}
				/>
			}
			{!isInsideModal && (
				<>
					<DeleteSpecificationConfirmModal
						open={deleteSpecModalOpen}
						setOpen={setDeleteSpecModalOpen}
						proceedAction={proceedAction}
					></DeleteSpecificationConfirmModal>
				</>
			)}
		</>
	);
}
